import React from "react";
import PropTypes from "prop-types";

interface ContextMenuItemProps {
    name?: string;
    active?: boolean;
    danger?: boolean;
    disabled?: boolean;
    // Element Types
    Icon?: React.ElementType;
    // Baseline arguments
    children?: React.ReactNode;
    className?: string;
    // Functions
    onClick?: () => void;
}

/**
 * An item for the `ContextMenu` Fusion component. These should populate the `children` field or be children of that component.
 * 
 * ## _
 * ### Arguments
 * @param {string} name - The text shown on the menu item.
 * @param {React.ElementType} Icon - A ReactComponent icon that is used on the left-hand side of the menu item.
 * @param {boolean} active - A flag to make the button "active", surrounding the icon in the active color.
 * @param {boolean} danger - A flag for making the menu item show a warning color.
 * @param {boolean} disabled - A flag for disabling the menu item. 
 * @param {React.ReactNode} children - The contents inside the menu item.
 * @param {string} className - CSS classes to apply to the menu item.
 * 
 * ## _
 * ### Functions
 * @param {function} onClick - Function called when the item is clicked.  
 */
class ContextMenuItem extends React.Component<ContextMenuItemProps> {
    static propTypes = {
        name: PropTypes.string,
        Icon: PropTypes.elementType,
        danger: PropTypes.bool,
        disabled: PropTypes.bool,
        onClick: PropTypes.func,
        children: PropTypes.node,
        className: PropTypes.string,
    };

    static defaultProps = {
        className: "",
        active: false,
        danger: false,
        disabled: false,
    };

    handleOnClick = () => {
        if (!this.props.disabled && this.props.onClick) {
            this.props.onClick();
        }
    }

    render() {
        const { name, Icon, active, danger, disabled, onClick, children, className, ...rest } = this.props;

        // Generate the list of classes for the inner a-icon-link component. TODO: replace with IconLink when made.
        let buttonClasses = "a-icon-link";

        if (danger) buttonClasses += " a-icon-link--destructive";
        if (active) buttonClasses += " a-icon-link--active";
        if (disabled) buttonClasses += " a-icon-link--disabled";
        
        buttonClasses += " a-icon-link--block";

        return (
            <li className={`m-context-menu__item ${className}`}>
                <button className={buttonClasses} onClick={this.handleOnClick}>
                    <div className="a-icon-link__inner">
                        <div className="a-icon-link__icon-wrapper">
                            {Icon && <Icon className="a-icon-link__icon" />}
                        </div>
                        <span className="a-icon-link__text">{name}</span>
                    </div>
                </button>
            </li>
        );
    }
}

export default ContextMenuItem;